import { useEffect, useRef, useState } from "react";

/**
 * useCounterUp — counts from 0 to `to` once the element enters the viewport.
 * Returns a number; use `format` in the component to render prefix/suffix.
 */
export function useCounterUp(to, { duration = 1800, threshold = 0.4 } = {}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const easeOut = (t) => 1 - Math.pow(1 - t, 3);
            const tick = (now) => {
              const elapsed = now - start;
              const p = Math.min(elapsed / duration, 1);
              const eased = easeOut(p);
              setValue(to * eased);
              if (p < 1) requestAnimationFrame(tick);
              else setValue(to);
            };
            requestAnimationFrame(tick);
            obs.unobserve(el);
          }
        });
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [to, duration, threshold]);

  return { ref, value };
}
